import { GoogleGenAI, Type } from "@google/genai";
import { AlternativeProduct, Recipe, ShoppingItem, Ingredient } from "../types";
import { functions } from "./firebase";
import { httpsCallable } from "firebase/functions";

// --- SECURITY & PROXY LAYER ---

// Terugvalketens per model dat in de code gebruikt wordt
const MODEL_FALLBACKS: Record<string, string[]> = {
    "gemini-3-flash-preview": ["gemini-3-flash-preview", "gemini-2.5-flash", "gemini-3.5-flash-lite"],
    "gemini-2.5-flash": ["gemini-3-flash-preview", "gemini-2.5-flash", "gemini-3.5-flash-lite"],
    "gemini-flash-lite-latest": ["gemini-3.5-flash-lite", "gemini-3-flash-preview", "gemini-2.5-flash"],
    "gemini-3-pro-preview": ["gemini-3.1-pro-preview", "gemini-2.5-pro", "gemini-3-flash-preview"],
};

// 404 (model weg), 429 (quotum), 5xx (overbelast) of "niet beschikbaar": volgende model proberen
const isRetryableModelError = (error: any): boolean => {
    const status = Number(error?.status ?? error?.code);
    const msg = String(error?.message || '').toLowerCase();
    return [404, 429, 500, 503].includes(status)
        || error?.name === 'AbortError' || error?.name === 'TimeoutError'
        || msg.includes('aborted') || msg.includes('timed out')
        || msg.includes('no longer available') || msg.includes('not found') || msg.includes('high demand');
};

// thinkingBudget is voor 2.5-modellen; Gemini 3 gebruikt thinkingLevel
const adaptThinkingConfig = (model: string, config: any) => {
    const tc = config?.thinkingConfig;
    if (!tc) return config;
    const isGemini3 = model.startsWith('gemini-3');
    if (isGemini3 && tc.thinkingBudget !== undefined) {
        const level = tc.thinkingBudget === 0 ? (model.includes('pro') ? 'LOW' : 'MINIMAL') : 'HIGH';
        return { ...config, thinkingConfig: { thinkingLevel: level } };
    }
    if (!isGemini3 && tc.thinkingLevel !== undefined) {
        return { ...config, thinkingConfig: { thinkingBudget: tc.thinkingLevel === 'MINIMAL' || tc.thinkingLevel === 'LOW' ? 0 : -1 } };
    }
    return config;
};

/**
 * Voert een Gemini API call uit.
 * 1. Als er een API Key beschikbaar is (lokaal of geïnjecteerd), gebruiken we de Client SDK direct.
 * 2. Als er GEEN key is maar wel Firebase functions, roepen we de Cloud Function aan.
 */
const executeGeminiCall = async (model: string, contents: any, config?: any, timeoutMs = 30000) => {
    // 1. Reconstruct Key from obfuscated globals (Vite injection)
    // @ts-ignore
    const p1 = typeof __GEMINI_KEY_P1__ !== 'undefined' ? __GEMINI_KEY_P1__ : "";
    // @ts-ignore
    const p2 = typeof __GEMINI_KEY_P2__ !== 'undefined' ? __GEMINI_KEY_P2__ : "";
    
    // Fallback: check env var directly
    const env = (import.meta as any).env || {};
    const envKey = env.VITE_GEMINI_API_KEY || env.GEMINI_API_KEY;

    const apiKey = (p1 && p2) ? (p1 + p2) : (p2 || envKey);

    // Modelkeuze met terugval. Welke modellen beschikbaar zijn hangt af van de API key
    // (bv. de 2.5-modellen zijn "niet meer beschikbaar voor nieuwe gebruikers"). Lukt een model
    // niet (niet beschikbaar, overbelast, quotum), dan proberen we het volgende.
    const candidates = MODEL_FALLBACKS[model] || [model];

    // A. DIRECT MODE (Development of Production met Key)
    if (apiKey && !apiKey.includes("YOUR_API_KEY")) {
        const client = new GoogleGenAI({ apiKey });
        let lastError: any = null;
        for (const candidate of candidates) {
            try {
                // @ts-ignore - SDK types en Proxy types matchen op generiek niveau
                const response = await client.models.generateContent({
                    model: candidate,
                    contents: contents,
                    config: {
                        ...adaptThinkingConfig(candidate, config),
                        // Preview-modellen reageren soms niet; na timeoutMs het volgende model proberen
                        abortSignal: AbortSignal.timeout(timeoutMs)
                    }
                });
                return {
                    text: response.text,
                    data: response
                };
            } catch (error: any) {
                lastError = error;
                if (!isRetryableModelError(error)) throw error;
                console.warn(`Gemini model ${candidate} niet bruikbaar, probeer volgende:`, error?.message || error);
            }
        }
        throw lastError;
    }

    const targetModel = candidates[0];

    // B. PROXY MODE (Production fallback if functions are initialized)
    if (functions) {
        try {
            const callGemini = httpsCallable(functions, 'callGemini');
            const result: any = await callGemini({
                model: targetModel,
                contents,
                config
            });
            
            const rawData = result.data;
            let text = "";
            if (rawData.candidates && rawData.candidates[0] && rawData.candidates[0].content && rawData.candidates[0].content.parts) {
                 text = rawData.candidates[0].content.parts.map((p: any) => p.text || "").join("");
            }

            return {
                text: text,
                data: rawData
            };
        } catch (error) {
            console.error("Gemini Proxy Error:", error);
            throw error;
        }
    }

    throw new Error("Geen actieve Gemini API sleutel geconfigureerd.");
};

const TAG_COLORS = [
    "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300 border border-green-200 dark:border-green-500/30",
    "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300 border border-amber-200 dark:border-amber-500/30",
    "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300 border border-blue-200 dark:border-blue-500/30",
];

export const compressImage = (base64Str: string): Promise<string> => {
    return new Promise((resolve) => {
        const img = new Image();
        img.src = base64Str;
        img.onload = () => {
            const canvas = document.createElement('canvas');
            // We behouden een redelijke resolutie voor de AI, maar niet te gek.
            // 1920px breed is meestal genoeg voor ingrediënt herkenning.
            const MAX_WIDTH = 1920; 
            let width = img.width;
            let height = img.height;
            
            if (width > MAX_WIDTH) {
                height = (height * MAX_WIDTH) / width;
                width = MAX_WIDTH;
            }
            
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) { resolve(base64Str); return; }
            
            ctx.drawImage(img, 0, 0, width, height);
            
            // Startkwaliteit
            let quality = 0.85;
            let newBase64 = canvas.toDataURL('image/jpeg', quality);
            
            // Strikte 1MB limiet (1MB ≈ 1.33 miljoen base64 karakters overhead meegerekend)
            // We verlagen kwaliteit agressief als het te groot is, want de AI heeft liever een iets waziger beeld dan geen beeld.
            const MAX_SIZE_CHARS = 1024 * 1024 * 1.33; 

            while (newBase64.length > MAX_SIZE_CHARS && quality > 0.2) {
                quality -= 0.1;
                newBase64 = canvas.toDataURL('image/jpeg', quality);
            }
            
            console.log(`Image compressed. Width: ${width}, Quality: ${quality.toFixed(1)}, Size: ${(newBase64.length / 1024 / 1.33).toFixed(0)}KB`);
            resolve(newBase64);
        };
        img.onerror = () => {
            console.error("Image compression failed, using original");
            resolve(base64Str); 
        };
    });
};

export const generateAlternatives = async (productName: string): Promise<AlternativeProduct[]> => {
  try {
    const response = await executeGeminiCall(
      "gemini-flash-lite-latest",
      `Analyseer '${productName}' en suggereer 3 alternatieven / vervangende producten voor de Nederlandse markt.
      
      BELANGRIJK VOOR ICONEN:
      Gebruik voor het veld 'icon' ALLEEN geldige Google Material Symbol namen in snake_case. 
      Gebruik bijvoorbeeld: 'eco' (voor duurzaam/bio), 'savings' (voor goedkoop), 'nutrition' (voor gezond), 'local_dining' (voor lekker), 'thumb_up' (voor populair).`,
      {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              name: { type: Type.STRING },
              description: { type: Type.STRING },
              tags: { 
                  type: Type.ARRAY, 
                  items: { 
                      type: Type.OBJECT, 
                      properties: { 
                          label: { type: Type.STRING }, 
                          icon: { type: Type.STRING, description: "Valid Material Symbol name like 'eco', 'bolt', 'star'" } 
                      } 
                  } 
              }
            }
          }
        }
      }
    );
    let text = response.text || "[]";
    const data = JSON.parse(text.replace(/```json/g, '').replace(/```/g, '').trim());
    return data.map((item: any, i: number) => ({
        ...item,
        tags: (item.tags || []).map((tag: any, j: number) => ({ ...tag, colorClass: TAG_COLORS[j % TAG_COLORS.length] }))
    }));
  } catch (error) { return []; }
};

export const SUPERMARKET_CATEGORIES = [
  "AGF (Groenten & Fruit)",
  "Zuivel, Eieren & Boter",
  "Bakkerij & Banket",
  "Vlees, Kip & Vis",
  "Voorraadkast & Kruiden",
  "Dranken & Sappen",
  "Snacks & Snoep",
  "Diepvries",
  "Huishoudelijk & Verzorging",
  "Overig",
];

const LOCAL_SUPERMARKET_MAP: Record<string, string[]> = {
  "AGF (Groenten & Fruit)": ["appel", "appels", "banaan", "bananen", "peer", "peren", "sla", "tomaat", "tomaten", "komkommer", "ui", "uien", "knoflook", "wortel", "wortels", "paprika", "champignon", "champignons", "aardappel", "aardappelen", "avocado", "citroen", "limoen", "sinaasappel", "sinaasappels", "mandarijn", "druiven", "aardbeien", "blauwe bessen", "spinazie", "broccoli", "courgette", "bloemkool", "boontjes", "sperziebonen", "prei", "gember", "peterselie", "koriander", "basilicum", "bieslook", "fruit", "groente", "groenten", "rucola", "andijvie", "zoete aardappel", "mango", "ananas", "kiwi", "radijs", "venkel", "aubergine", "spruitjes", "rode kool", "witlof"],
  "Zuivel, Eieren & Boter": ["melk", "yoghurt", "kaas", "ei", "eieren", "boter", "roomboter", "kwark", "room", "slagroom", "kookroom", "creme fraiche", "mozzarella", "parmezaan", "pecorino", "feta", "havermelk", "amandelmelk", "sojamelk", "goudse", "vla", "karnemelk", "margarine", "skyr", "geitenkaas", "mascarpone", "ricotta", "halvarine"],
  "Bakkerij & Banket": ["brood", "bolletjes", "broodjes", "croissant", "croissants", "stokbrood", "pita", "wrap", "wraps", "beschuit", "crackers", "cake", "koek", "ontbijtkoek", "krentenbollen", "tortilla", "tortillas", "naan", "bagels", "pistolets"],
  "Vlees, Kip & Vis": ["kip", "kipfilet", "kippendijen", "gehakt", "biefstuk", "spek", "spekjes", "pancetta", "zalm", "tonijn", "garnalen", "worst", "worstjes", "rookworst", "vlees", "vis", "ham", "kalkoen", "shoarma", "hamburger", "hamburgers", "salami", "kabeljauw", "varkenshaas", "ribeye", "rundvlees", "lamsvlees", "bacon", "filet americain", "kipstukjes"],
  "Voorraadkast & Kruiden": ["pasta", "rijst", "spaghetti", "macaroni", "penne", "noedels", "meel", "bloem", "suiker", "zout", "peper", "olijfolie", "olie", "azijn", "sojasaus", "bouillon", "bouillonblokje", "tomatenblokjes", "tomatenpuree", "passata", "kruiden", "curry", "pindakaas", "jam", "hagelslag", "honing", "saus", "mayonaise", "ketchup", "mosterd", "bonen", "kikkererwten", "linzen", "couscous", "havermout", "muesli", "cornflakes", "kokosmelk", "paprikapoeder", "kaneel", "oregano", "pesto", "cacao", "appelmoes", "soep"],
  "Dranken & Sappen": ["koffie", "koffiebonen", "thee", "water", "spa", "sap", "sinaasappelsap", "appelsap", "jus d orange", "cola", "bier", "wijn", "rode wijn", "witte wijn", "frisdrank", "limonade", "siroop", "ice tea", "tonic", "prosecco"],
  "Snacks & Snoep": ["chips", "noten", "chocolade", "snoep", "popcorn", "borrelnootjes", "koekjes", "drop", "pindas", "nootjes", "repen", "zoutjes", "tortillachips", "stroopwafels"],
  "Diepvries": ["ijs", "ijsblokjes", "pizza", "diepvries", "diepvriesgroente", "diepvrieserwtjes", "erwtjes", "vissticks", "friet", "patat", "kroketten", "frikandellen", "bitterballen"],
  "Huishoudelijk & Verzorging": ["afwasmiddel", "vaatwastabletten", "wasmiddel", "wasverzachter", "shampoo", "conditioner", "zeep", "handzeep", "douchegel", "tandpasta", "tandenborstel", "deodorant", "wc-papier", "toiletpapier", "keukenrol", "vuilniszakken", "allesreiniger", "schoonmaakmiddel", "aluminiumfolie", "vershoudfolie", "luiers", "batterijen", "sponsjes"]
};

const normalizeItemName = (name: string) =>
  name.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9 -]/g, ' ').replace(/\s+/g, ' ').trim();

/**
 * Snelle lokale categorisering (zonder AI).
 * Exacte (meerwoordige) match wint; daarna woorden die beginnen of eindigen op een trefwoord
 * (bv. "kipfilet" -> kip, "volkorenbrood" -> brood). Korte trefwoorden ("ui", "ei") alleen exact.
 */
export const categorizeLocally = (name: string): string | null => {
  const normalized = normalizeItemName(name);
  if (!normalized) return null;
  const words = normalized.split(' ');
  let best: { cat: string; score: number } | null = null;

  for (const [cat, keywords] of Object.entries(LOCAL_SUPERMARKET_MAP)) {
    for (const rawKeyword of keywords) {
      const k = normalizeItemName(rawKeyword);
      let score = 0;
      if (normalized === k) score = 1000 + k.length;
      else if (k.includes(' ') && normalized.includes(k)) score = 500 + k.length;
      else if (words.includes(k)) score = 300 + k.length;
      // Samenstellingen: het laatste deel bepaalt het product ("ijsbergsla" is sla, geen ijs)
      else if (k.length >= 3 && words.some(w => w.endsWith(k))) score = 100 + k.length;
      else if (k.length >= 3 && words.some(w => w.startsWith(k))) score = k.length;
      if (score > 0 && (!best || score > best.score)) best = { cat, score };
    }
  }
  return best ? best.cat : null;
};

// Eerder door AI bepaalde categorieën onthouden, zodat hetzelfde product nooit opnieuw naar de AI gaat
const CATEGORY_CACHE_KEY = 'supermarct-category-cache';

const loadCategoryCache = (): Record<string, string> => {
  try { return JSON.parse(localStorage.getItem(CATEGORY_CACHE_KEY) || '{}'); } catch { return {}; }
};

const saveCategoryCache = (cache: Record<string, string>) => {
  try { localStorage.setItem(CATEGORY_CACHE_KEY, JSON.stringify(cache)); } catch { /* opslag vol: geen probleem */ }
};

/** Direct beschikbare categorie (cache of lokaal), zonder AI. */
export const getInstantCategory = (name: string): string | null =>
  loadCategoryCache()[normalizeItemName(name)] || categorizeLocally(name);

// Snelste modellen eerst; thinking uit voor lage latency
const CATEGORIZE_MODELS: { model: string; config: any }[] = [
  { model: 'gemini-3.5-flash-lite', config: { thinkingConfig: { thinkingLevel: 'MINIMAL' } } },
  { model: 'gemini-3-flash-preview', config: { thinkingConfig: { thinkingLevel: 'MINIMAL' } } },
  { model: 'gemini-2.5-flash-lite', config: { thinkingConfig: { thinkingBudget: 0 } } },
];

const categorizeWithAI = async (names: string[]): Promise<Record<string, string>> => {
  const prompt = `Deel elk product in bij één afdeling van een Nederlandse supermarkt. Geef voor elk product (index i) de categorie.\nProducten: ${JSON.stringify(names.map((n, i) => ({ i, n })))}`;
  for (const { model, config } of CATEGORIZE_MODELS) {
    try {
      const response = await executeGeminiCall(model, prompt, {
        ...config,
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              i: { type: Type.INTEGER },
              c: { type: Type.STRING, enum: SUPERMARKET_CATEGORIES }
            },
            required: ["i", "c"]
          }
        }
      }, 8000);
      const parsed: { i: number; c: string }[] = JSON.parse((response.text || "[]").replace(/```json/g, '').replace(/```/g, '').trim());
      const result: Record<string, string> = {};
      for (const { i, c } of parsed) {
        if (names[i] && SUPERMARKET_CATEGORIES.includes(c)) result[names[i]] = c;
      }
      return result;
    } catch (error) {
      console.warn(`Categoriseren met ${model} mislukt`, error);
    }
  }
  throw new Error("AI categorisering niet beschikbaar");
};

/**
 * Categoriseert boodschappen. Bekende producten (cache + lokale lijst) zijn direct klaar;
 * alleen onbekende producten gaan (in één keer) naar de AI.
 */
export const categorizeItems = async (items: { id: string, name: string }[], useAI = true): Promise<{ id: string, category: string }[]> => {
  const cache = loadCategoryCache();
  const results: Record<string, string> = {};
  const unknownNames = new Set<string>();

  for (const item of items) {
    const instant = cache[normalizeItemName(item.name)] || categorizeLocally(item.name);
    if (instant) results[item.id] = instant;
    else unknownNames.add(item.name);
  }

  if (unknownNames.size > 0) {
    if (useAI) try {
      const aiResult = await categorizeWithAI(Array.from(unknownNames));
      for (const [name, cat] of Object.entries(aiResult)) cache[normalizeItemName(name)] = cat;
      saveCategoryCache(cache);
    } catch (error) {
      console.warn("AI categorisering mislukt, gebruik 'Overig'", error);
    }
    for (const item of items) {
      if (!results[item.id]) results[item.id] = cache[normalizeItemName(item.name)] || "Overig";
    }
  }

  return items.map(i => ({ id: i.id, category: results[i.id] }));
};

const COACH_SYSTEM_INSTRUCTION = `Je bent de Koelkast Coach van superMarct.
DOEL: Genereer een passend gerecht met de beschikbare producten.
STRIKTE REGEL: Geef ALTIJD een recept terug. Geen uitzonderingen.
LOGICA: Verwerk zoveel mogelijk van de beschikbare ingrediënten (inclusief die uit missingIngredients die zijn toegevoegd) in de bereidingsstappen.`;

const COACH_RESPONSE_SCHEMA = {
  type: Type.OBJECT,
  properties: {
    type: { type: Type.STRING, enum: ['existing', 'new'] },
    existingRecipeId: { type: Type.STRING },
    newRecipe: { 
      type: Type.OBJECT,
      properties: {
        title: { type: Type.STRING },
        time: { type: Type.STRING },
        category: { type: Type.STRING, enum: ['Diner', 'Lunch', 'Ontbijt', 'Vega'] },
        ingredients: { type: Type.ARRAY, items: { type: Type.OBJECT, properties: { name: { type: Type.STRING }, amount: { type: Type.STRING } } } },
        steps: { type: Type.ARRAY, items: { type: Type.STRING } }
      }
    },
    missingIngredients: { 
      type: Type.ARRAY, 
      items: { type: Type.OBJECT, properties: { name: { type: Type.STRING }, amount: { type: Type.STRING } } } 
    },
    coachTip: { type: Type.STRING }
  }
};

export const getCoachRecommendation = async (inventory: string, existingRecipes: Recipe[]) => {
  try {
    const titles = existingRecipes.map(r => ({ id: r.id, title: r.title }));
    const response = await executeGeminiCall(
      "gemini-2.5-flash",
      `Restjes: "${inventory}". Bestaand: ${JSON.stringify(titles)}.`,
      {
        systemInstruction: COACH_SYSTEM_INSTRUCTION,
        responseMimeType: "application/json",
        responseSchema: COACH_RESPONSE_SCHEMA
      }
    );
    let text = response.text || "{}";
    return JSON.parse(text.replace(/```json/g, '').replace(/```/g, '').trim());
  } catch (error) {
    // Intelligent local fallback if offline or no key configured
    const cleanInv = inventory.trim() || "groenten en restjes";
    return {
      type: 'new',
      newRecipe: {
        title: `Snelle Roerbak met ${cleanInv.split(/[,+\n]/)[0] || 'Restjes'}`,
        time: '15 min',
        category: 'Diner',
        ingredients: [
          ...cleanInv.split(/[,+\n]/).filter(s => s.trim().length > 0).map(s => ({ name: s.trim(), amount: 'naar smaak' })),
          { name: 'Olijfolie of boter', amount: '1 el' },
          { name: 'Peper en zout', amount: 'snufje' }
        ],
        steps: [
          `Verhit wat olie of boter in een koekenpan of wok op middelhoog vuur.`,
          `Snijd ${cleanInv} in hapklare stukken en voeg toe aan de hete pan.`,
          `Bak alles 6-8 minuten al omscheppend tot het beetgaar en geurig is.`,
          `Breng op smaak met zout, peper en eventueel kruiden uit je voorraadkast. Serveer direct warm!`
        ]
      },
      missingIngredients: [],
      coachTip: "Tip: Bewaar restjes altijd goed afgesloten in een bakje in de koelkast om verspilling tegen te gaan."
    };
  }
};

export const getCoachRecommendationFromPhoto = async (base64Image: string, existingRecipes: Recipe[]) => {
  try {
    // 1. Schoon base64 op
    const cleanBase64 = base64Image.replace(/^data:image\/(png|jpg|jpeg);base64,/, "");
    
    // 2. Comprimeer nogmaals voor de zekerheid (garantie < 1MB)
    const compressedImage = await compressImage(base64Image); 
    const cleanCompressed = compressedImage.replace(/^data:image\/(png|jpg|jpeg);base64,/, "");

    const titles = existingRecipes.map(r => ({ id: r.id, title: r.title }));
    
    // 3. Gebruik Gemini 3 Flash Preview (goede vision capabilities)
    const response = await executeGeminiCall(
      "gemini-3-flash-preview", 
      {
        parts: [
          { inlineData: { mimeType: "image/jpeg", data: cleanCompressed } },
          { text: `Kijk goed naar deze foto van een koelkast of voorraadkast.
                   1. Identificeer ALLE zichtbare eetbare producten en ingrediënten.
                   2. Bedenk op basis daarvan een lekker, logisch recept.
                   3. Je mag basisingrediënten (olie, kruiden, water) als aanwezig beschouwen.
                   4. Als er producten missen voor een compleet gerecht, zet die bij 'missingIngredients'.
                   
                   Bestaande recepten in database (kies hier een van als het heel erg lijkt): ${JSON.stringify(titles)}.` }
        ]
      },
      {
          systemInstruction: COACH_SYSTEM_INSTRUCTION,
          responseMimeType: "application/json",
          responseSchema: COACH_RESPONSE_SCHEMA
      }
    );

    let text = response.text || "{}";
    return JSON.parse(text.replace(/```json/g, '').replace(/```/g, '').trim());
  } catch (error) { 
      console.error("Coach Photo Error", error);
      return null; 
  }
};

export const refineCoachRecipe = async (previousRecipe: any, userPrompt: string, availableIngredients: string[] = []) => {
  const contextMessage = availableIngredients.length > 0 
    ? `\nExtra beschikbaar: ${availableIngredients.join(', ')}. VERWERK DEZE IN STAPPEN.`
    : "";
  try {
    const response = await executeGeminiCall(
      "gemini-3-flash-preview",
      `Pas aan: ${JSON.stringify(previousRecipe)}.${contextMessage} Feedback: "${userPrompt}".`,
      {
        systemInstruction: COACH_SYSTEM_INSTRUCTION,
        responseMimeType: "application/json",
        responseSchema: COACH_RESPONSE_SCHEMA
      }
    );
    let text = response.text || "{}";
    return JSON.parse(text.replace(/```json/g, '').replace(/```/g, '').trim());
  } catch (error) { return null; }
};

// Afbeeldingsmodellen in volgorde van voorkeur. Als een model niet beschikbaar is voor deze
// API key (404/403) of geen afbeelding teruggeeft, proberen we het volgende.
const IMAGE_MODELS = [
    'gemini-nano-banana-2.1',
    'gemini-3.1-flash-image',
    'gemini-2.5-flash-image',
];

const blobToDataUrl = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });

// Gratis route zonder API key (Pollinations.ai, FLUX). Gemini-beeldmodellen hebben geen gratis tier.
const generateImageWithPollinations = async (prompt: string): Promise<string | null> => {
    const seed = Math.floor(Math.random() * 1_000_000);
    const url = `https://image.pollinations.ai/prompt/${encodeURIComponent(prompt)}?width=1024&height=768&model=flux&nologo=true&seed=${seed}`;
    const response = await fetch(url, { signal: AbortSignal.timeout(60000) });
    if (!response.ok) throw new Error(`Pollinations ${response.status}`);
    const blob = await response.blob();
    if (!blob.type.startsWith('image/')) throw new Error(`Pollinations gaf geen afbeelding (${blob.type})`);
    return compressImage(await blobToDataUrl(blob));
};

export const generateRecipeImage = async (recipeTitle: string): Promise<string | null> => {
    const prompt = `A realistic, appetizing food photograph of the dish "${recipeTitle}". Natural light, served on a plate, shot from a 45 degree angle. No text, no watermark.`;

    try {
        const image = await generateImageWithPollinations(prompt);
        if (image) return image;
    } catch (error) {
        console.warn('Pollinations afbeelding mislukt, probeer Gemini:', error);
    }

    for (const model of IMAGE_MODELS) {
        try {
            const response = await executeGeminiCall(
                model,
                { parts: [{ text: prompt }] },
                { responseModalities: ['TEXT', 'IMAGE'] }
            );

            // Werkt voor zowel de directe SDK-response als de proxy (raw data)
            const parts = response.data?.candidates?.[0]?.content?.parts || [];
            for (const part of parts) {
                if (part.inlineData?.data) {
                    return await compressImage(`data:${part.inlineData.mimeType || 'image/png'};base64,${part.inlineData.data}`);
                }
            }
            console.warn(`Image model ${model} gaf geen afbeelding terug`, response.text);
        } catch (error) {
            console.warn(`Image model ${model} faalde:`, error);
        }
    }
    return null;
};

export const parseRecipeFromImage = async (base64Image: string): Promise<Recipe | null> => {
    try {
        const cleanBase64 = base64Image.replace(/^data:image\/(png|jpg|jpeg);base64,/, "");
        const response = await executeGeminiCall(
            "gemini-3-flash-preview", 
            { parts: [{ inlineData: { mimeType: "image/jpeg", data: cleanBase64 } }, { text: `Digitize this recipe into Dutch JSON.` }] },
            {
                responseMimeType: "application/json",
                responseSchema: {
                    type: Type.OBJECT,
                    properties: {
                        title: { type: Type.STRING },
                        time: { type: Type.STRING },
                        category: { type: Type.STRING, enum: ['Diner', 'Lunch', 'Ontbijt', 'Vega'] },
                        ingredients: { type: Type.ARRAY, items: { type: Type.OBJECT, properties: { name: { type: Type.STRING }, amount: { type: Type.STRING } } } },
                        steps: { type: Type.ARRAY, items: { type: Type.STRING } }
                    }
                }
            }
        );
        let text = response.text || "[]";
        text = text.replace(/```json/g, '').replace(/```/g, '').trim();
        const data = JSON.parse(text);
        
        const compressedOriginal = await compressImage(base64Image);
        return {
            id: Date.now().toString(),
            title: data.title || "Gescand Recept",
            image: compressedOriginal,
            time: data.time || "30 min",
            category: data.category || "Diner",
            ingredients: data.ingredients || [],
            steps: data.steps || [],
            isFavorite: false,
            status: 'ready'
        };
    } catch (error) { return null; }
};

export const parseRecipeFromUrl = async (url: string): Promise<Recipe | null> => {
  try {
    // Gebruik Gemini 3 Flash voor betere redenering en search capabilities
    const response = await executeGeminiCall(
      "gemini-3-pro-preview",
      `Gebruik Google Search om het recept op deze URL te vinden: ${url}.
      
      TAAK:
      1. Zoek de inhoud van de pagina.
      2. Extraheer de titel, bereidingstijd, ingrediënten en stappen.
      3. Neem de stappen zo exact mogelijk over (vertaal indien nodig naar Nederlands).
      4. Formatteer het resultaat als een JSON object.
      
      Output JSON formaat:
      {
        "title": "Naam van recept",
        "time": "bijv. 30 min",
        "category": "Diner" (of Lunch, Ontbijt, Vega),
        "ingredients": [{"name": "item", "amount": "hoeveelheid"}],
        "steps": ["stap 1", "stap 2"]
      }`,
      {
        tools: [{ googleSearch: {} }],
        responseMimeType: "application/json"
      }
    );

    let text = response.text || "{}";
    
    // Robuuste parsing: zoek naar het eerste { en het laatste }
    // Dit voorkomt issues als het model markdown code blocks of introductie tekst toevoegt
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
        text = jsonMatch[0];
    } else {
        // Fallback cleanup
        text = text.replace(/```json/g, '').replace(/```/g, '').trim();
    }

    const data = JSON.parse(text);
    
    const aiImage = await generateRecipeImage(data.title);

    return {
      id: Date.now().toString(),
      title: data.title || "Geïmporteerd Recept",
      image: aiImage || "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&w=800&q=80",
      time: data.time || "30 min",
      category: data.category || "Diner",
      ingredients: data.ingredients || [],
      steps: data.steps || [],
      isFavorite: false,
      status: 'ready'
    };
  } catch (error) {
    console.error("URL Import failed", error);
    return null;
  }
};